import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { toast } from "sonner";
import ShieldLogo from "./ShieldLogo";

const HERO_IMG =
    "https://images.unsplash.com/photo-1778103617525-76877c583fa5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwc2VtaSUyMHRydWNrJTIwaGlnaHdheSUyMGFtZXJpY2FuJTIwcm9hZHxlbnwwfHx8fDE3ODg0NzczMjV8MA&ixlib=rb-4.1.0&q=85&w=1200";

const LINES = [
    { text: "PUT CHA", cls: "text-[#F6F2E8]" },
    { text: "FOOT IN IT,", cls: "text-[#D32F2F]" },
    { text: "DRIVER!", cls: "text-[#F6F2E8]" },
];

const EASE = [0.16, 1, 0.3, 1];

const portalToast = (who) =>
    toast(who === "driver" ? "DRIVER AUTHORIZATION PORTAL" : "FLEET OPERATIONS PORTAL", {
        description: "Portal is staged and ready — production credentials coming soon. Keep the shiny side up.",
    });

export default function Hero() {
    const navigate = useNavigate();
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 16 });
    const sy = useSpring(my, { stiffness: 60, damping: 16 });
    const rotateY = useTransform(sx, [-0.5, 0.5], [8, -8]);
    const rotateX = useTransform(sy, [-0.5, 0.5], [-6, 6]);
    const chipX = useTransform(sx, [-0.5, 0.5], [20, -20]);
    const chipY = useTransform(sy, [-0.5, 0.5], [14, -14]);

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            ref={ref}
            onMouseMove={onMove}
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden px-6 pb-16 pt-32 lg:px-16"
        >
            <div className="halftone-bg pointer-events-none absolute inset-0 opacity-[0.07]" />
            <div className="pointer-events-none absolute -left-40 top-1/3 h-[500px] w-[500px] rounded-full bg-[#D32F2F]/10 blur-[120px]" />
            <div className="pointer-events-none absolute -right-40 bottom-0 h-[500px] w-[500px] rounded-full bg-[#0055A5]/15 blur-[120px]" />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
                        data-testid="hero-badge"
                        className="inline-block border-2 border-[#0E0D0C] bg-[#E5A93C] px-4 py-1.5 shadow-[3px_3px_0px_#0E0D0C]"
                    >
                        <span className="font-mono2 text-xs font-bold tracking-[0.3em] text-[#161413]">
                            ★ OFFICIAL TRUCK-LEGAL ROUTING ENGINE ★
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-title"
                        className="font-display mt-6 text-[clamp(3.5rem,9.5vw,8.5rem)] uppercase leading-[0.9] tracking-wide"
                    >
                        {LINES.map((l, i) => (
                            <span key={l.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${l.cls}`}
                                    initial={{ y: "110%" }}
                                    animate={{ y: "0%" }}
                                    transition={{ duration: 0.9, delay: 0.25 + i * 0.15, ease: EASE }}
                                >
                                    {l.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
                        className="hazard mt-4 h-3 w-56 origin-left border-2 border-[#0E0D0C]"
                    />

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.85, ease: EASE }}
                        data-testid="hero-tagline"
                        className="font-type mt-6 max-w-xl text-lg text-[#E5A93C] sm:text-xl"
                    >
                        Hammer Down. Dodge the Low Bridges. Keep the Wheels Turnin'.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.95, ease: EASE }}
                        className="font-mono2 mt-3 max-w-xl text-sm leading-relaxed text-[#C2C7CE]"
                    >
                        Truck-legal routing, bridge clearance alerts, live weigh station status and diesel
                        bargains — built for 18-wheel warriors and the fleets behind them.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 1.0, ease: EASE }}
                        data-testid="hero-federal-safety"
                        className="mt-5 max-w-xl border-2 border-[#E5A93C] bg-[#161413]/85 px-4 py-3 shadow-[4px_4px_0px_#0E0D0C]"
                    >
                        <p className="font-display text-lg tracking-widest text-[#E5A93C]">
                            FEDERAL HIGHWAYS FIRST — SAFER MILES
                        </p>
                        <p className="font-mono2 mt-1 text-[11px] leading-relaxed tracking-wider text-[#F6F2E8]">
                            We route drivers onto Interstates and U.S. highways as much as possible, using
                            state and county roads only when needed to complete the trip — avoiding surprises
                            on rough, unkempt back roads.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 1.05, ease: EASE }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <button
                            data-testid="hero-driver-login-btn"
                            onClick={() => navigate("/login")}
                            className="btn-press font-display border-2 border-[#0E0D0C] bg-[#D32F2F] px-8 py-4 text-xl tracking-widest text-[#F6F2E8] shadow-[4px_4px_0px_#0E0D0C] hover:bg-[#B71C1C]"
                        >
                            DRIVER LOGIN
                        </button>
                        <button
                            data-testid="hero-fleets-btn"
                            onClick={() => navigate("/fleet")}
                            className="btn-press font-display border-2 border-[#0E0D0C] bg-[#0055A5] px-8 py-4 text-xl tracking-widest text-[#F6F2E8] shadow-[4px_4px_0px_#0E0D0C] hover:bg-[#003B73]"
                        >
                            FLEET OPERATIONS
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.2 }}
                        data-testid="hero-telemetry-strip"
                        className="font-mono2 mt-10 inline-flex flex-wrap items-center gap-x-6 gap-y-2 border-2 border-[#E5A93C]/40 bg-[#0E0D0C]/70 px-4 py-3 text-xs tracking-widest text-[#C2C7CE]"
                    >
                        <span>
                            ROUTE: <b className="text-[#F6F2E8]">I-80 EAST</b>
                        </span>
                        <span>
                            BRIDGE: <b className="text-[#F6F2E8]">14'6"</b>
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-[#2E7D32]" />
                            SCALES: <b className="text-[#2E7D32]">OPEN</b>
                        </span>
                        <span>
                            CB CH-19: <b className="text-[#F6F2E8]">LIVE</b>
                        </span>
                    </motion.div>
                </div>

                <div className="relative lg:col-span-5" style={{ perspective: 1000 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.5, ease: EASE }}
                        style={{ rotateX, rotateY, rotate: 2 }}
                        data-testid="hero-photo-frame"
                        className="relative border-2 border-[#0E0D0C] bg-[#24211E] shadow-[10px_10px_0px_#0E0D0C]"
                    >
                        <img
                            src={HERO_IMG}
                            alt="Vintage semi truck rolling down an American highway"
                            className="h-[380px] w-full object-cover contrast-125 saturate-75 sepia-[0.35] sm:h-[440px]"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_15%,rgba(229,169,60,0.28),transparent_55%)]" />
                        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(14,13,12,0.75),transparent_45%)]" />
                        <div className="font-mono2 absolute bottom-0 left-0 right-0 flex items-center justify-between border-t-2 border-[#0E0D0C] bg-[#161413] px-4 py-2 text-[10px] tracking-[0.25em] text-[#E5A93C]">
                            <span>UNIT 66 — WESTBOUND</span>
                            <span>0422 HRS</span>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 1, ease: EASE }}
                        style={{ x: chipX, y: chipY }}
                        data-testid="hero-shield-chip"
                        className="absolute -left-8 -top-8 drop-shadow-[6px_6px_0_rgba(14,13,12,0.9)]"
                    >
                        <ShieldLogo size={120} topText="PUT CHA" number="66" />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
